import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getAssets, generateBatch, invalidateAsset, reactivateAsset,
  getBatchList, batchPrintUrl, batchCsvUrl, batchZipUrl
} from '../../services/adminApi';
import { toast } from 'react-toastify';
import config from '../../config/appConfig';

const ITEM_TYPES = ['card', 'bracelet', 'keychain', 'sticker', 'tag', 'pendant', 'badge'];
const STATUS_COLORS = { available: 'badge-blue', registered: 'badge-green', invalidated: 'badge-red' };
const INVALIDATE_REASONS = ['lost', 'damaged', 'stolen', 'recalled', 'defective', 'other'];

// ── Generate batch modal ───────────────────────────────────────
function GenerateModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ count: 10, itemType: 'card', batchNumber: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);

  const token = localStorage.getItem('adminToken');

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await generateBatch({
        count: Number(form.count),
        itemType: form.itemType,
        batchNumber: form.batchNumber || undefined
      });
      toast.success(`Generated ${res.data.assets?.length || res.data.count} QR codes`);
      setResult(res.data);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Generation failed');
    } finally { setLoading(false); }
  };

  const openPrintSheet = () => {
    const url = batchPrintUrl(result.batchNumber);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.text())
      .then(html => {
        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
      })
      .catch(() => toast.error('Failed to open print sheet'));
  };

  const downloadCsv = () => {
    const url = batchCsvUrl(result.batchNumber);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${result.batchNumber}-manifest.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Failed to download CSV'));
  };

  const downloadZip = () => {
    const url = batchZipUrl(result.batchNumber);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${result.batchNumber}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Failed to download ZIP'));
  };

  // ── Success: show download options ──────────────────────
  if (result) {
    return (
      <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ maxWidth: 480 }}>
          <div className="modal-header">
            <h3>✓ Batch Generated</h3>
            <button className="btn-icon" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--green-dim)', border: '1px solid #065f46', borderRadius: 'var(--radius)', padding: '14px 16px', color: 'var(--green)', fontSize: 14, fontWeight: 600 }}>
              ✓ {result.assets?.length || result.count} QR codes generated
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
              <div><span style={{ color: 'var(--text3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.6px' }}>Batch</span><div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 2 }}>{result.batchNumber}</div></div>
              <div><span style={{ color: 'var(--text3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.6px' }}>Type</span><div style={{ textTransform: 'capitalize', marginTop: 2 }}>{form.itemType}</div></div>
            </div>
            <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>Download for Production</div>
              <button className="btn btn-primary" onClick={downloadZip} style={{ justifyContent: 'center' }}>📦 Download Production ZIP</button>
              <p style={{ fontSize: 11, color: 'var(--text3)', margin: '-4px 0 0', textAlign: 'center' }}>Individual QR images + CSV manifest + printable sheet — everything in one file.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button className="btn btn-secondary" onClick={openPrintSheet} style={{ justifyContent: 'center' }}>🖨 Print Sheet</button>
                <button className="btn btn-secondary" onClick={downloadCsv} style={{ justifyContent: 'center' }}>📋 CSV Only</button>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>▣ Generate QR Code Batch</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row col2">
              <div>
                <label className="form-label">Quantity (1–1000)</label>
                <input className="admin-input" type="number" min={1} max={1000} value={form.count} onChange={e => setForm(f => ({ ...f, count: e.target.value }))} required />
              </div>
              <div>
                <label className="form-label">Product Type</label>
                <select className="admin-select" value={form.itemType} onChange={e => setForm(f => ({ ...f, itemType: e.target.value }))} style={{ width: '100%' }}>
                  {ITEM_TYPES.map(t => (<option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>))}
                </select>
              </div>
            </div>
            <div>
              <label className="form-label">Batch Number (optional)</label>
              <input className="admin-input" placeholder={`BATCH-${new Date().toISOString().slice(0,10)}`} value={form.batchNumber} onChange={e => setForm(f => ({ ...f, batchNumber: e.target.value }))} />
            </div>
            <div style={{ marginTop: 14, padding: 12, background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--text3)' }}>
              Each QR code: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{config.assetPrefix}-XXXXXXXX</span> linked to <strong>{config.domain}</strong>.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" type="button" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? <span className="spinner" /> : `Generate ${form.count} Codes`}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Invalidate modal ───────────────────────────────────────────
function InvalidateModal({ assetId, onClose, onSuccess }) {
  const [reason, setReason] = useState('lost');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await invalidateAsset(assetId, reason);
      toast.success(`Asset ${assetId} invalidated`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to invalidate');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h3>✕ Invalidate QR Code</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
              Invalidating{' '}
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{assetId}</span>{' '}
              will immediately prevent it from displaying any profile data when scanned.
            </p>
            <label className="form-label">Reason</label>
            <select className="admin-select" style={{ width: '100%' }} value={reason} onChange={e => setReason(e.target.value)}>
              {INVALIDATE_REASONS.map(r => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" type="button" onClick={onClose}>Cancel</button>
            <button className="btn btn-danger" type="submit" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Invalidate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function AdminAssets() {
  const [assets, setAssets]     = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage]         = useState(1);
  const [showGenerate, setShowGenerate] = useState(false);
  const [invalidateId, setInvalidateId] = useState(null);
  const [batches, setBatches]   = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(true);

  const token = localStorage.getItem('adminToken');

  const load = useCallback(() => {
    setLoading(true);
    getAssets({
      page, limit: 30,
      search: search || undefined,
      status: statusFilter || undefined,
      type: typeFilter || undefined
    })
      .then(r => { setAssets(r.data.assets); setPagination(r.data.pagination); })
      .catch(() => toast.error('Failed to load assets'))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, typeFilter]);

  const loadBatches = useCallback(() => {
    setBatchesLoading(true);
    getBatchList()
      .then(r => setBatches(r.data.batches || []))
      .catch(() => {})
      .finally(() => setBatchesLoading(false));
  }, []);

  useEffect(() => { load(); loadBatches(); }, [load, loadBatches]);

  const handleReactivate = async assetId => {
    try {
      await reactivateAsset(assetId);
      toast.success('Asset reactivated');
      load();
    } catch { toast.error('Failed to reactivate'); }
  };

  const openPrintSheet = batchNumber => {
    const url = batchPrintUrl(batchNumber);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.text())
      .then(html => {
        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
      })
      .catch(() => toast.error('Failed to open print sheet'));
  };

  const downloadCsv = batchNumber => {
    const url = batchCsvUrl(batchNumber);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${batchNumber}-manifest.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Failed to download CSV'));
  };

  const downloadZip = batchNumber => {
    const url = batchZipUrl(batchNumber);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${batchNumber}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Failed to download ZIP'));
  };

  return (
    <AdminLayout title="QR Codes">
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input
            className="admin-input"
            placeholder={`Search by ID e.g. ${config.assetPrefix}-…`}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select className="admin-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="available">Available</option>
          <option value="registered">Registered</option>
          <option value="invalidated">Invalidated</option>
        </select>
        <select className="admin-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {ITEM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>
            + Generate Batch
          </button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
        {pagination.total ?? '—'} total · {pagination.total ? `page ${page} of ${pagination.pages}` : ''}
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Asset ID</th>
              <th>Type</th>
              <th>Batch</th>
              <th>Status</th>
              <th>Registered To</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>
                  ))}
                </tr>
              ))
            ) : assets.length === 0 ? (
              <tr><td colSpan={7}>
                <div className="empty-state">
                  <div className="empty-icon">▣</div>
                  <p>No QR codes found. Generate a batch to get started.</p>
                </div>
              </td></tr>
            ) : assets.map(a => (
              <tr key={a._id}>
                <td className="mono">{a.assetId}</td>
                <td style={{ textTransform: 'capitalize', color: 'var(--text2)' }}>{a.type || a.assetType || '—'}</td>
                <td className="mono" style={{ fontSize: 11 }}>{a.batchNumber || '—'}</td>
                <td>
                  <span className={`badge ${STATUS_COLORS[a.status] || 'badge-grey'}`}>
                    {a.status}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {a.userId ? `${a.userId.firstName} ${a.userId.lastName}` : '—'}
                </td>
                <td style={{ color: 'var(--text3)', fontSize: 12 }}>
                  {new Date(a.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {a.status !== 'invalidated' ? (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setInvalidateId(a.assetId)}
                      >
                        Invalidate
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleReactivate(a.assetId)}
                      >
                        Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagination.pages > 1 && (
          <div className="pagination">
            <span className="page-info">Page {page} of {pagination.pages}</span>
            <div className="page-btns">
              <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <button className="page-btn" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Batch History ─────────────────────────────────────── */}
      <div className="admin-table-wrap" style={{ marginTop: 20 }}>
        <div className="admin-table-header">
          <h3>📦 Batch History</h3>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            {batches.length} batch{batches.length !== 1 ? 'es' : ''}
          </span>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Batch Number</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Created</th>
              <th>Downloads</th>
            </tr>
          </thead>
          <tbody>
            {batchesLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>
                  ))}
                </tr>
              ))
            ) : batches.length === 0 ? (
              <tr><td colSpan={5}>
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <p>No batches yet. Generate your first batch above.</p>
                </div>
              </td></tr>
            ) : batches.map(b => (
              <tr key={b.batchNumber}>
                <td className="mono">{b.batchNumber}</td>
                <td style={{ textTransform: 'capitalize', color: 'var(--text2)' }}>{b.assetType}</td>
                <td style={{ fontWeight: 600 }}>{b.count}</td>
                <td style={{ color: 'var(--text3)', fontSize: 12 }}>
                  {new Date(b.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => downloadZip(b.batchNumber)}>
                      📦 ZIP
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => openPrintSheet(b.batchNumber)}>
                      🖨 Print
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => downloadCsv(b.batchNumber)}>
                      📋 CSV
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showGenerate && (
        <GenerateModal onClose={() => setShowGenerate(false)} onSuccess={() => { load(); loadBatches(); }} />
      )}
      {invalidateId && (
        <InvalidateModal
          assetId={invalidateId}
          onClose={() => setInvalidateId(null)}
          onSuccess={load}
        />
      )}
    </AdminLayout>
  );
}
